import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getLogoSettings, saveLogoSettings} from 'src/openapi/admin/apis/ConferenceLogoApi';
import {useAppDispatch} from 'src/store/hooks';
import {base64ToFile} from 'src/utils/base64ToFile';

import {
    BANNER_IMAGE_CONTROL,
    BANNER_IMAGE_REQUIRED_ERROR,
    BANNER_IMAGE_REQUIRED_MESSAGE,
} from '../constants';

import type {SettingsType} from 'src/openapi/admin/models/SettingsType';
import type {SuccessPayload} from 'src/typedef';

type Props = {
    conferenceId: number,
    settingsType: SettingsType,
};

export type BannerSettings = {
    link?: string,
    content?: File,
};

export const useBanner = ({conferenceId, settingsType}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getLogoSettings({conferenceId, settingsType}, {signal}));
        if (response.payload.success) {
            return Promise.resolve({type: '', payload: {
                success: true,
                error: false,
                data: {
                    content: response.payload.data.content
                        ? base64ToFile(response.payload.data.content)
                        : undefined,
                    link: response.payload.data.link || undefined,
                },
            } as SuccessPayload<BannerSettings>});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [conferenceId, apiDispatch, settingsType]);

    const saveDispatchAction = useCallback((bannerSettings: BannerSettings, signal: AbortSignal) => apiDispatch(
        saveLogoSettings({
            logo: bannerSettings.content,
            link: bannerSettings.link,
            conferenceId,
            settingsType,
        }, {signal}),
    ), [conferenceId, apiDispatch, settingsType]);

    const editValidation = useCallback((bannerSettings: BannerSettings) => {
        if (bannerSettings.content || !bannerSettings.link) {
            removeError(`${settingsType}-${BANNER_IMAGE_CONTROL}`);
        }
    }, [settingsType, removeError]);

    const saveValidation = useCallback((bannerSettings: BannerSettings) => {
        let valid = true;

        if (!bannerSettings.content && bannerSettings.link) {
            addError({
                controlId: `${settingsType}-${BANNER_IMAGE_CONTROL}`,
                code: BANNER_IMAGE_REQUIRED_ERROR,
                message: BANNER_IMAGE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [settingsType, addError]);

    return useCommonDataHook({
        initValue: {},
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
